/**
 * The landing below the manifesto (ADR-0029): the proof panel, the program desk, the terminal, the path to a payout,
 * the documents, the questions, the closing call and the footer. One editorial grid on black; the proof and the
 * closing call carry the brand's two fields of colour, lime and cobalt.
 */
import type { Lang } from "@prop/i18n";
import { localeTagFor } from "@prop/i18n";
import { useMemo } from "react";
import { Link } from "wouter";
import { useLandingT, useLang, useLegalT } from "../../i18n";
import { Configurator } from "./configurator/Configurator";
import { art, artBase, MARKETS, num, SHOT_WIDTHS, shot, shotBase, useQuality, useTape } from "./landing-data";
import { Pic } from "./Pic";
import { CountUp, LiveTape } from "./LiveBits";
import { Buy } from "./Buy";
import { Mark } from "./Mark";
import { openConsentSettings } from "../../lib/consent";

const arrow = "↗";

/** Section head: a wide title on the left, its lead (and the buy button, where the section earns one) on the right. */
function Head({ id, title, lead, buy }: { id: string; title: string; lead?: string; buy?: boolean }) {
  return (
    <div className="head" data-rise>
      <h2 id={id}>{title}</h2>
      {(lead || buy) && (
        <div className="head-side">
          {lead && <p>{lead}</p>}
          {buy && <Buy />}
        </div>
      )}
    </div>
  );
}

/** The brand's voice in one running line: the slogans twice over, so the loop never shows a seam. */
export function Slogans() {
  const t = useLandingT();
  const keys = ["s1", "s2", "s3", "s4", "s5", "s6"] as const;
  return (
    <section className="slogans" aria-label={keys.map((k) => t(`slogans.${k}`)).join(". ")}>
      <ul aria-hidden="true">
        {[...keys, ...keys].map((k, i) => (
          <li key={i}>{t(`slogans.${k}`)}</li>
        ))}
      </ul>
    </section>
  );
}

/** One of the desk's own rules, set large on a whole field of colour. Never attributed to anyone it did not come from. */
export function Quote({ k, tone }: { k: "money" | "start"; tone: "cobalt" | "lime" }) {
  const t = useLandingT();
  return (
    <section className={`quote ${tone}`}>
      <blockquote data-rise>
        <p>{t(`quotes.${k}.text`)}</p>
        <footer>{t(`quotes.${k}.by`)}</footer>
        <div className="quote-buy">
          <Buy ink={tone === "lime"} />
        </div>
      </blockquote>
    </section>
  );
}

/** The proof in public: execution quality over the last 7 days, in one bordered panel. */
export function Trust() {
  const t = useLandingT();
  const { lang } = useLang();
  const l = lang as Lang;
  const d = useQuality().data;
  const time = useMemo(() => new Intl.DateTimeFormat(localeTagFor(l), { hour: "2-digit", minute: "2-digit", second: "2-digit" }), [l]);
  const ms = (v: { p50: number; p99: number } | null | undefined) => (v ? t("board.ms", { a: num(v.p50, l), b: num(v.p99, l) }) : "—");
  return (
    <section className="trust wrap" id="execution" aria-labelledby="q-h">
      <div className="panel edge-glow">
        <div className="panel-head" data-rise>
          <h2 id="q-h">{t("board.title")}</h2>
          <p>{t("board.lead")}</p>
          <p className="panel-time">
            <span className="dot" aria-hidden="true" /> {d ? t("board.updated", { time: time.format(new Date(d.generatedAt)) }) : t("board.loading")}
          </p>
        </div>
        <div className="cards">
          <article className="card lime edge-glow-hover" data-rise>
            <p className="card-big">
              <CountUp value={d?.fills} format={(v) => num(v, l)} />
            </p>
            <p className="card-k">{t("board.fills")}</p>
            <p className="card-note">{d ? `${num(d.accounts, l)} ${t("board.accounts")}` : ""}</p>
            {d && !d.latencyMs && <p className="card-note">{t("board.withheld")}</p>}
          </article>
          <article className="card cobalt edge-glow-hover" data-rise>
            <p className="card-mid">{ms(d?.latencyMs)}</p>
            <p className="card-k">{t("board.latency")}</p>
            <p className="card-mid sm">{ms(d?.quoteAgeMs)}</p>
            <p className="card-k">{t("board.quoteAge")}</p>
          </article>
          <article className="card graphite edge-glow-hover" data-rise>
            <p className="card-mid">{d?.walkedPct == null ? "—" : `${num(d.walkedPct, l, 1)}%`}</p>
            <p className="card-k">{t("board.walked")}</p>
            <div className="meter" aria-hidden="true">
              <span style={{ width: `${d?.walkedPct ?? 0}%` }} />
            </div>
            <p className="card-note">
              {t("board.policy")} {d?.policyVersion ? `v${d.policyVersion}` : "—"}
              {d?.lastFillAt ? ` · ${t("board.lastFill", { time: time.format(new Date(d.lastFillAt)) })}` : ""}
            </p>
          </article>
          <figure className="card pic edge-glow-hover" data-rise>
            <Pic base={artBase("streak")} widths={[640, 1280]} sizes="(max-width: 1100px) 50vw, 30vw" src={art("streak")} width={1280} height={720} />
          </figure>
        </div>
      </div>
      <div className="receipt" data-rise>
        <div>
          <h3>{t("board.receipt")}</h3>
          <p className="soft">{t("board.receiptLead")}</p>
        </div>
        <Pic base={shotBase(lang, "fill-snapshot")} widths={SHOT_WIDTHS} sizes="(max-width: 1100px) 100vw, 65vw" src={shot(lang, "fill-snapshot")} width={2880} height={832} alt={t("pics.fill")} />
      </div>
    </section>
  );
}

/** The program desk: the full challenge configurator on black (the app's dark tokens), pinned price bar on a phone. */
export function Programs() {
  const t = useLandingT();
  return (
    <section className="block wrap" id="programs" aria-labelledby="p-h">
      <Head id="p-h" title={t("spec.title")} lead={t("spec.lead")} />
      <div className="dark cfg">
        <Configurator stickyBottom="bottom-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" summaryTop="lg:top-6" />
        <p className="footnote">{t("spec.footnote")}</p>
      </div>
    </section>
  );
}

export function Platform() {
  const t = useLandingT();
  const { lang } = useLang();
  const items = ["f1", "f2", "f3", "f4"] as const;
  return (
    <section className="block" id="platform" aria-labelledby="pl-h">
      <div className="wrap">
        <Head id="pl-h" title={t("platform.title")} lead={t("platform.lead")} buy />
      </div>
      <LiveTape items={useTape(lang as Lang)} label={t("hero.tape")} />
      <div className="wrap">
        <figure className="screen-stage">
          <div className="screen edge-glow">
            <Pic base={shotBase(lang, "terminal")} widths={SHOT_WIDTHS} sizes="(max-width: 1440px) 100vw, 1440px" src={shot(lang, "terminal")} width={2880} height={1220} alt={t("pics.terminal")} />
          </div>
          <figcaption className="soft small">{t("platform.caption")}</figcaption>
        </figure>
        <ul className="cols">
          {items.map((k) => (
            <li key={k} data-rise>
              <h3>{t(`platform.${k}.t`)}</h3>
              <p className="soft">{t(`platform.${k}.d`)}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function How() {
  const t = useLandingT();
  return (
    <section className="block wrap" id="how" aria-labelledby="how-h">
      <Head id="how-h" title={t("how.title")} buy />
      <ol className="steps">
        {(["s1", "s2", "s3", "s4"] as const).map((s, i) => (
          <li key={s} className="edge-glow-hover" data-rise>
            <span className="step-n" aria-hidden="true">
              {i + 1}
            </span>
            <h3>{t(`how.${s}.line`)}</h3>
            <p className="soft">{t(`how.${s}.note`)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function Docs() {
  const t = useLandingT();
  const docs = [
    ["execution", "/legal/execution"],
    ["rules", "/programs"],
    ["terms", "/legal/terms"],
    ["payouts", "/legal/payouts"],
    ["refunds", "/legal/refunds"],
    ["prohibited", "/legal/prohibited"],
    ["risk", "/legal/risk"],
    ["privacy", "/legal/privacy"],
  ] as const;
  return (
    <section className="block wrap" id="docs" aria-labelledby="docs-h">
      <Head id="docs-h" title={t("docs.title")} lead={t("docs.lead")} />
      <ul className="rows">
        {docs.map(([k, href]) => (
          <li key={k} data-rise>
            <Link href={href} className="row edge-glow-hover">
              <span className="row-name">{t(`docs.${k}.name`)}</span>
              <span className="soft">{t(`docs.${k}.meta`)}</span>
              <span className="row-open">
                {t("docs.open")} <span aria-hidden="true">{arrow}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Faq() {
  const t = useLandingT();
  const keys = ["real", "fills", "day", "rules", "feed", "payouts"] as const;
  return (
    <section className="block wrap faq" id="faq" aria-labelledby="faq-h">
      <Head id="faq-h" title={t("faq.title")} buy />
      <div className="qa edge-glow">
        {keys.map((k) => (
          <details key={k} data-rise>
            <summary>{t(`faq.${k}.q`)}</summary>
            <p className="soft">{t(`faq.${k}.a`)}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** The closing call returns to the hero's paper: the title at full width, one action, the three markets. */
export function Final({ start }: { start: string }) {
  const t = useLandingT();
  return (
    <section className="final" aria-labelledby="final-h">
      <div className="wrap final-in">
        <h2 id="final-h">{t("cta.title")}</h2>
        <div className="final-row">
          <p>{t("cta.lead")}</p>
          <div className="final-actions">
            <Buy />
            <Link href={start} className="hx-link">
              {t("cta.secondary")}
            </Link>
          </div>
        </div>
        <div className="final-art" aria-hidden="true">
          {MARKETS.map((m) => (
            <Pic key={m} base={artBase(m)} widths={[200, 400]} sizes="10vw" src={art(m)} width={400} height={400} />
          ))}
        </div>
      </div>
    </section>
  );
}

/** Every legal document, in the order of the legal page (i18n/legalDocs.ts keeps the texts out of this bundle). */
const LEGAL_LINKS = ["terms", "refunds", "payouts", "prohibited", "aml", "privacy", "cookies", "risk", "execution"] as const;

export function Footer() {
  const t = useLandingT();
  const tl = useLegalT();
  return (
    <footer className="foot">
      <div className="wrap">
        <p className="risk">{t("footer.risk")}</p>
        <div className="foot-grid">
          <div>
            <p>{t("footer.note")}</p>
            <a className="soft" href={`mailto:${t("footer.contact")}`}>
              {t("footer.contact")}
            </a>
          </div>
          <nav aria-label={t("nav.menu")}>
            <a href="#programs">{t("nav.programs")}</a>
            <a href="#execution">{t("nav.execution")}</a>
            <a href="#faq">{t("nav.faq")}</a>
            <Link href="/login">{t("nav.signIn")}</Link>
          </nav>
          <nav aria-label={t("nav.docs")}>
            {LEGAL_LINKS.map((d) => (
              <Link key={d} href={`/legal/${d}`}>
                {tl(`nav.${d}`)}
              </Link>
            ))}
            <button type="button" className="foot-btn" onClick={openConsentSettings}>
              {tl("consent.open")}
            </button>
          </nav>
        </div>
        <p className="foot-word" aria-hidden="true">
          <Mark size={112} /> {t("brand")}
        </p>
        <div className="foot-meta soft">
          <span>
            © {t("footer.year")} {t("brand")}
          </span>
          <span>{t("footer.credits")}</span>
          <a href="#top">{t("footer.top")} ↑</a>
        </div>
      </div>
    </footer>
  );
}
